import React, { useEffect, useState, useContext } from 'react'
import { components, constants, useServices, useTranslation } from 'cng-web-lib'
import AciHighwayCargoApiUrls from 'src/apiUrls/AciHighwayCargoApiUrls'
import AciHighwayResponseApiUrls from 'src/apiUrls/AciHighwayResponseApiUrls'
import AciHighwayManifestKeys from 'src/constants/locale/key/AciHighwayManifest'
import AciHighwayKeys from 'src/constants/locale/key/AciHighwayResponse'
import Namespace from 'src/constants/locale/Namespace'
import moment from 'moment-timezone'
import {
  Grid,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@material-ui/core'
import clsx from 'clsx'
import ISF5ResponseApiUrls from 'src/apiUrls/ISF5ResponseApiUrls'
import { AceIsfTranslationContext } from '../contexts/ACEISFTranslationContext'

const {
  table: { useFetchCodeMaintenanceLookup }
} = components

const {
  filter: { EQUAL }
} = constants

const useStyles = makeStyles((theme) => ({
  table: {
    '& .MuiTableHead-root': {
      backgroundColor: theme.palette.background.sectionOddBg,
      '& .MuiTableCell-head': {
        borderBottom: 0,
        fontSize: 12,
        fontWeight: 700,
        lineHeight: 1.2,
        textTransform: 'uppercase'
      }
    },
    '& .MuiTableBody-root': {
      '& .MuiTableRow-root': {
        '&:last-child': {
          '& .MuiTableCell-root': {
            borderBottom: 0
          }
        }
      }
    },
    '& .MuiTableCell-root': {
      padding: '8px 16px'
    }
  }
}))

function Response(props) {
  const { showNotification, id, isViewOnly = false } = props

  const [data, setData] = useState([])
  const [lookups, setLookups] = useState(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const { fetchRecord, fetchRecords } = useServices()
  const { translate } = useTranslation([Namespace.ACE_HIGHWAY_RESPONSE])
  const fetchCodeMaintenanceLookup = useFetchCodeMaintenanceLookup()
  const classes = useStyles()
  const { getTranslatedText } = useContext(AceIsfTranslationContext)

  function getCargo() {
    return fetchRecord.execute(AciHighwayCargoApiUrls.GET, id, (res) => res.content[0])
  }

  function fetchISF5Response() {
    return fetchRecords.execute(ISF5ResponseApiUrls.GET,
      {
        filters: [
          {
            field: 'headerId',
            operator: 'equal',
            value: id
          }
        ]
      }, (res) => res)
  }

  function showISF5Response() {
    Promise.all([fetchISF5Response()]).then(([respData]) => setData(respData))
  }

  useEffect(() => {
    showISF5Response();
    Promise.all([
      fetchCodeMaintenanceLookup(
        constants.CodeMaintenanceType.CODE_MASTER,
        undefined,
        [
          {
            field: 'codeType',
            operator: EQUAL,
            value: 'ACEISF_ACTION_CODE'
          }
        ],
        undefined,
        'code'
      ),
    ]).then(([actionCodes]) => {
      setLookups({ actionCodes})
    })
  }, [])

  const columns = [
    {
      field: 'transactionNo',
      title: getTranslatedText('ACE_ISF', 'cbpTransactionNo')
    },
    {
      field: 'receivedDate',
      title: getTranslatedText('ACE_ISF', 'receivedDate'),
      render: (data) =>
        data.receivedDate &&
        moment(data.receivedDate).tz("Canada/Eastern").format('D MMM YYYY HH:mm')
    },
    {
      field: 'submissionAction',
      title: getTranslatedText('ACE_ISF', 'actionCode'),
      render: (data) =>
        lookups
          ? lookups['actionCodes'][data.submissionAction]
          : data.submissionAction
    },
    {
      field: 'statusCode',
      title: getTranslatedText('ACE_ISF', 'statusCode'),
      render: (data) => (
        <Grid container spacing={1}>
          {data.isf5ResponseErrorDTOList.map((response, index) => (
            <Grid key={index} item xs={12}>
              <Typography variant='body2'>{response.errorCode}</Typography>
              <Typography color='textSecondary' variant='caption'>
                      {response.description}
                      </Typography>
            </Grid>
          ))}
        </Grid>
      )
    },
    {
      field: 'errorCode',
      title: getTranslatedText('ACE_ISF', 'errorCode')
    },
    {
      field: 'description',
      title: getTranslatedText('ACE_ISF', 'description')
    },
    {
      field: 'remarks',
      title: getTranslatedText('ACE_ISF', 'remarks')
    }
  ]

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value)
    setPage(0)
  }

  return (
    <>
      <TableContainer>
        <Table className={clsx(classes.table, 'new-ui-table-component__table')}>
          <TableHead>
            <TableRow>
              {columns.map((column, index) => (
                <TableCell key={index}>{column.title}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length > 0 ? (
              data
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((d) => (
                  <TableRow key={d.id}>
                    {columns.map((column, index) => (
                      <TableCell key={index}>
                        {column.render ? column.render(d) : d[column.field]}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
            ) : (
              <TableRow>
                <TableCell align='center' colSpan={columns.length}>
                  <Typography variant='body2' color='textSecondary'>
                    No records to display
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 20, 50, 100, 500, 2000]}
        component='div'
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
      />
    </>
  )
}

export default Response
